import React, { useEffect, useState } from 'react';
import { reportsAPI, assessmentsAPI } from '../services/api';
import { Assessment } from '../types';
import { useAuth } from '../context/AuthContext';

const ReportsPage: React.FC = () => {
  const { organisationId } = useAuth();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [generating, setGenerating] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    assessmentsAPI.list().then((res) => {
      setAssessments(res.data.filter((a: Assessment) => a.status !== 'draft'));
      setLoading(false);
    });
  }, []);

  const downloadReport = async (assessmentId: number) => {
    setGenerating(assessmentId);
    try {
      const res = await reportsAPI.generate(assessmentId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `POSH_Report_Assessment_${assessmentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Report generation failed. Ensure scoring is complete.');
    } finally {
      setGenerating(null);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">PDF Reports</h1>
        <p className="text-sm text-gray-500 mt-0.5">Generate and download POSH compliance assessment reports</p>
      </div>

      {assessments.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <span className="text-5xl">📄</span>
          <p className="mt-3">No submitted assessments yet. Complete an assessment to generate a report.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Assessment</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Score</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Maturity</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Report</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((a) => (
                <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">Assessment #{a.id}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium capitalize ${
                      a.status === 'finalised' ? 'bg-green-100 text-green-700'
                      : a.status === 'reviewed' ? 'bg-blue-100 text-blue-700'
                      : 'bg-amber-100 text-amber-700'
                    }`}>{a.status}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 font-semibold">
                    {a.total_score != null ? `${a.total_score.toFixed(1)}%` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {a.maturity_level && (
                      <span className="text-xs font-bold px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                        {a.maturity_level}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    }) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => downloadReport(a.id)}
                      disabled={generating === a.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors"
                    >
                      {generating === a.id ? (
                        <>
                          <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full" />
                          Generating…
                        </>
                      ) : (
                        <> 📥 Download PDF </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Report contents info */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-blue-800 mb-2">What's in the report?</h2>
        <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
          <li>Executive Summary — overall score and maturity level</li>
          <li>Organisation Profile — industry, ICC composition, policy status</li>
          <li>Category-wise Score Breakdown with visual bars</li>
          <li>Compliance Gaps — priority-ordered (HIGH / MEDIUM / LOW)</li>
          <li>Actionable Recommendations per maturity level</li>
          <li>Evidence Summary — uploaded vs required</li>
          <li>ICC Member Snapshot</li>
          <li>Disclaimer and confidentiality notice</li>
        </ul>
      </div>
    </div>
  );
};

export default ReportsPage;
